import { Plus, Trash2 } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { Link, useParams, useSearchParams } from 'react-router';
import { toast } from 'sonner';
import { controlesDe } from '@/api/client';
import { useActionsPret, useEcheancierPret, useInstructeursPrets, usePret } from '@/api/hooks';
import { LIBELLE_ETAPE, LIBELLE_MOTIF_AVIS, MOTIFS_AVIS, type DecisionApi, type DetailPret, type DevisePret, type EcheancePret, type EngagementSaisie, type MotifAvis, type ResultatSimulation, type SensAvis } from '@/api/types';
import { BoutonExcel, BoutonPdf, Chargement, CircuitTimeline, ErreurBloc, Historique, Kpi, PageHeader } from '@/components/common';
import { PiecesJointes } from '@/components/pieces-jointes';
import { ControlesConge, EtapesPrevues, montantDevise, StatutPretBadge } from '@/components/rh';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { SelectNatif } from '@/components/ui/select-natif';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { dateFr, dateHeureFr } from '@/lib/utils';
import { PretAccorde } from './PretAccorde';

const LIBELLE_DECISION: Record<DecisionApi, string> = { VALIDER: 'Valider', COMMENTER: 'Commenter', CORRIGER: 'Demander un complément', REJETER: 'Rejeter', ANNULER: 'Annuler' };
const pct = (x: number | null | undefined) => (x === null || x === undefined ? '—' : `${x.toLocaleString('fr-FR')} %`);
const auj = () => new Date().toISOString().slice(0, 10);

/** Dossier de prêt (§ 5.11) : onglets Demande, Plafond, Simulation et conditions, Examen, Circuit, Pièces ; actions selon le rôle. */
export function PretDossier() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const { data, isLoading, error } = usePret(id);
  const actions = useActionsPret();
  const [dialogue, setDialogue] = useState<null | 'plafond' | 'confirmer' | 'decision' | 'affecter' | 'analyse' | 'conditions' | 'reponse' | 'suspendre' | 'retirer' | 'resoumettre' | 'engagements'>(null);
  // Onglet initial par l'adresse (?onglet=examen) : liens des notifications et captures de la documentation.
  const [onglet, setOnglet] = useState(() => (['demande', 'plafond', 'simulation', 'examen', 'circuit', 'pieces', 'remboursement'].includes(params.get('onglet') ?? '') ? params.get('onglet')! : 'demande'));
  if (isLoading) return <Chargement />;
  if (error || !data) return <ErreurBloc erreur={error} />;
  const d = data;
  const q = d.demande;
  const a = d.actions;
  const dev = q.devise;
  const executer = async (fn: () => Promise<unknown>, succes: string) => {
    try {
      await fn();
      toast.success(succes);
      setDialogue(null);
    } catch (e) {
      const controles = controlesDe(e);
      toast.error(controles.length ? controles.filter((c) => !c.ok).map((c) => c.message).join(' ') : e instanceof Error ? e.message : 'Action impossible.');
    }
  };
  return (
    <div>
      <PageHeader
        titre={
          <span className="flex flex-wrap items-center gap-2">
            {q.numero ?? 'Demande de prêt'} <StatutPretBadge statut={q.statut} />
            {q.statut === 'SOUMIS' && q.etapeCourante && <Badge variant="secondary">{q.conditionsEnAttente ? 'Conditions à accepter' : LIBELLE_ETAPE[q.etapeCourante]}</Badge>}
          </span>
        }
        sousTitre={`${q.libelleNature} · ${q.agent.nom} · ${q.structure.libelle} · déposée le ${dateHeureFr(q.dateDepot)}${q.dateDecision ? ` · décidée le ${dateHeureFr(q.dateDecision)}` : ''}`}
        retour={
          <Link to="/rh/prets" className="text-sm text-muted-foreground hover:underline">
            ← Prêts sociaux
          </Link>
        }
        actions={
          <div className="flex flex-wrap gap-2">
            <BoutonPdf chemin={`/pdf/prets/${q.id}`} nom={`${q.numero ?? 'demande-pret'}.pdf`} libelle="Formulaire PDF" />
            {a.saisirPlafond && <Button onClick={() => setDialogue('plafond')}>{d.plafond ? 'Recalculer le plafond' : 'Saisir le plafond'}</Button>}
            {a.confirmer && <Button onClick={() => setDialogue('confirmer')}>Confirmer le dossier</Button>}
            {a.repondreConditions && <Button onClick={() => setDialogue('reponse')}>Répondre aux conditions</Button>}
            {a.decider && <Button onClick={() => setDialogue('decision')}>Décider</Button>}
            {a.analyser && (
              <Button variant="outline" onClick={() => setDialogue('analyse')}>
                Établir la fiche d'analyse
              </Button>
            )}
            {a.affecter && (
              <Button variant="outline" onClick={() => setDialogue('affecter')}>
                {q.instructeur ? 'Réaffecter' : 'Affecter à la Division des investissements'}
              </Button>
            )}
            {a.modifierConditions && (
              <Button variant="outline" onClick={() => setDialogue('conditions')}>
                Modifier les conditions
              </Button>
            )}
            {a.resoumettre && <Button onClick={() => setDialogue('resoumettre')}>Soumettre le complément</Button>}
            {a.suspendre && (
              <Button variant="outline" onClick={() => setDialogue('suspendre')}>
                Suspendre
              </Button>
            )}
            {a.reprendre && (
              <Button variant="outline" onClick={() => executer(() => actions.reprendre.mutateAsync(q.id), 'Dossier repris.')} disabled={actions.reprendre.isPending}>
                Reprendre
              </Button>
            )}
            {a.retirer && (
              <Button variant="outline" onClick={() => setDialogue('retirer')}>
                Retirer la demande
              </Button>
            )}
          </div>
        }
      />
      {q.statut === 'SUSPENDU' && (
        <Card className="mb-4 border-amber-300">
          <CardHeader>
            <CardTitle className="text-base">Dossier suspendu{q.suspenduLe ? ` le ${dateHeureFr(q.suspenduLe)}` : ''}</CardTitle>
            <CardDescription>{q.motifSuspension}</CardDescription>
          </CardHeader>
        </Card>
      )}
      {q.statut === 'ANNULE' && q.motifCloture && (
        <Card className="mb-4">
          <CardHeader>
            <CardTitle className="text-base">Demande annulée</CardTitle>
            <CardDescription>{q.motifCloture}</CardDescription>
          </CardHeader>
        </Card>
      )}
      {d.conditionsEnAttente && (
        <Card className="mb-4 border-sky-300" data-testid="conditions-en-attente">
          <CardHeader>
            <CardTitle className="text-base">Nouvelles conditions proposées (version {d.conditionsEnAttente.version})</CardTitle>
            <CardDescription>
              {d.conditionsEnAttente.auteur === 'DIRECTEUR_FINANCIER' ? 'Le Directeur financier' : 'Le Directeur Général'} propose {montantDevise(d.conditionsEnAttente.montant, dev)} sur {d.conditionsEnAttente.dureeMois} mois, mensualité {montantDevise(d.conditionsEnAttente.mensualite, dev)}, total à rembourser {montantDevise(d.conditionsEnAttente.total, dev)}. {d.conditionsEnAttente.commentaire} Le circuit reprend après l'acceptation de l'agent ; un refus annule la demande.
            </CardDescription>
          </CardHeader>
        </Card>
      )}

      <Tabs value={onglet} onValueChange={setOnglet}>
        <TabsList className="mb-4 flex-wrap">
          <TabsTrigger value="demande">Demande</TabsTrigger>
          <TabsTrigger value="plafond">Plafond</TabsTrigger>
          <TabsTrigger value="simulation">Simulation et conditions</TabsTrigger>
          <TabsTrigger value="examen">Examen</TabsTrigger>
          <TabsTrigger value="circuit">Circuit</TabsTrigger>
          <TabsTrigger value="pieces">Pièces</TabsTrigger>
          {['APPROUVE', 'DECAISSE', 'EN_REMBOURSEMENT', 'SOLDE'].includes(q.statut) && <TabsTrigger value="remboursement">Décaissement et remboursement</TabsTrigger>}
        </TabsList>

        <TabsContent value="demande" className="space-y-4">
          <div className="grid gap-4 lg:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle>I. Demandeur</CardTitle>
                <CardDescription>Repris du référentiel des agents et de la situation confidentielle.</CardDescription>
              </CardHeader>
              <CardContent className="grid gap-3 text-sm sm:grid-cols-2">
                <Champ l="Noms et prénom" v={d.identite.nom} />
                <Champ l="Matricule" v={d.identite.matricule} />
                <Champ l="Fonction" v={d.identite.fonction} />
                <Champ l="Grade" v={d.identite.grade ?? '—'} />
                <Champ l="Direction / service" v={d.identite.structure} />
                <Champ l="Date d'engagement" v={d.identite.dateEngagement ? `${dateFr(d.identite.dateEngagement)} (${d.identite.moisDeService} mois de service)` : '—'} />
                {d.situation && <Champ l="Salaire net contractuel" v={d.situation.salaireNet === null ? '—' : montantDevise(d.situation.salaireNet, d.situation.devise as DevisePret)} />}
                {d.situation && <Champ l="Départ à la retraite prévu" v={d.situation.dateRetraitePrevue ? dateFr(d.situation.dateRetraitePrevue) : '—'} />}
                {!d.eligibilite.eligible && <Champ l="Éligibilité" v={d.eligibilite.motif ?? ''} />}
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle>II. Nature, montant et modalités</CardTitle>
              </CardHeader>
              <CardContent className="grid gap-3 text-sm sm:grid-cols-2">
                <Champ l="Nature du prêt" v={q.libelleNature} />
                <Champ l="Devise" v={dev} />
                <Champ l="Montant souhaité" v={montantDevise(q.montantSouhaite, dev)} />
                <Champ l="Montant confirmé" v={q.montantConfirme === null ? 'à fixer par simulation' : montantDevise(q.montantConfirme, dev)} />
                <Champ l="Modalités de remboursement" v={q.dureeMois ? `En ${q.dureeMois} mois · ${montantDevise(q.mensualite ?? 0, dev)} par mois` : '—'} />
                <Champ l="Montant en lettres" v={d.montantEnLettres ?? '—'} />
                {q.objet && <Champ l="Objet" v={q.objet} />}
                {q.observations && <Champ l="Observations des RH" v={q.observations} />}
              </CardContent>
            </Card>
          </div>
          <Card>
            <CardHeader>
              <CardTitle className="flex flex-wrap items-center justify-between gap-2">
                III. Prêts en cours (engagements existants)
                {a.modifierEngagements && (
                  <Button size="sm" variant="outline" onClick={() => setDialogue('engagements')}>
                    Modifier
                  </Button>
                )}
              </CardTitle>
              <CardDescription>Déclarés par l'agent, vérifiés par les Ressources humaines ; les engagements dont le rachat est prévu sont exclus des contrôles.</CardDescription>
            </CardHeader>
            <CardContent>
              {d.engagements.length === 0 ? (
                <p className="text-sm text-muted-foreground">Existence de prêt en cours : non.</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Banque / FONAREV</TableHead>
                      <TableHead className="text-right">Montant</TableHead>
                      <TableHead className="text-right">Mensualité</TableHead>
                      <TableHead className="text-right">Maturité</TableHead>
                      <TableHead>Décaissement</TableHead>
                      <TableHead className="text-right">Solde restant dû</TableHead>
                      <TableHead>Statut</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {d.engagements.map((e) => (
                      <TableRow key={e.id}>
                        <TableCell>{e.source === 'FONAREV' ? 'FONAREV' : e.banque}</TableCell>
                        <TableCell className="num text-right">{montantDevise(e.montant, e.devise)}</TableCell>
                        <TableCell className="num text-right">{montantDevise(e.mensualite, e.devise)}</TableCell>
                        <TableCell className="num text-right">{e.maturiteMois} mois</TableCell>
                        <TableCell>{e.dateDecaissement ? dateFr(e.dateDecaissement) : '—'}</TableCell>
                        <TableCell className="num text-right">{montantDevise(e.soldeRestantDu, e.devise)}</TableCell>
                        <TableCell className="space-x-1">
                          <Badge variant={e.statut === 'ETEINT' ? 'outline' : e.statut === 'RACHAT_PREVU' ? 'secondary' : 'default'}>{e.statut === 'ETEINT' ? 'Éteint' : e.statut === 'RACHAT_PREVU' ? 'Rachat prévu' : 'En cours'}</Badge>
                          {e.verifieLe && <Badge variant="outline">vérifié RH</Badge>}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="plafond" className="space-y-4">
          <Card data-testid="plafond">
            <CardHeader>
              <CardTitle>Plafond communiqué par les Ressources humaines</CardTitle>
              <CardDescription>Décompte final estimé × quotité − capacité déjà mobilisée (soldes restants dus et plafonds réservés par d'autres demandes). Le plafond s'entend du montant total à rembourser, intérêts compris ; sa communication n'est pas une décision d'octroi.</CardDescription>
            </CardHeader>
            <CardContent>
              {d.plafond ? (
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                  <Kpi label="Décompte final estimé" valeur={montantDevise(d.plafond.decompteFinal, d.plafond.devise)} detail={`calculé le ${dateFr(d.plafond.dateCalcul)} par ${d.plafond.calculePar}`} />
                  <Kpi label={`Quotité mobilisable`} valeur={pct(d.plafond.quotite)} detail={`au plus ${d.parametres.quotiteMax} %`} />
                  <Kpi label="Capacité déjà mobilisée" valeur={montantDevise(d.plafond.capaciteMobilisee, d.plafond.devise)} detail="soldes restants dus et autres demandes" />
                  <Kpi label="Plafond disponible" valeur={montantDevise(d.plafond.plafondDisponible, d.plafond.devise)} detail={`valable jusqu'au ${dateFr(d.plafond.valideJusquAu)}${d.plafond.expire ? ' · expiré' : ''}`} accent={d.plafond.expire ? 'critique' : 'ok'} />
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">{q.statut === 'PLAFOND_DEMANDE' ? 'En attente du calcul des Ressources humaines.' : 'Aucun plafond communiqué.'}</p>
              )}
              {d.plafond?.observations && <p className="mt-3 text-sm">{d.plafond.observations}</p>}
              {d.plafonds > 1 && <p className="mt-2 text-xs text-muted-foreground">{d.plafonds} calculs conservés ; le dernier fait foi.</p>}
            </CardContent>
          </Card>
          {d.situation && (
            <Card>
              <CardHeader>
                <CardTitle>Situation confidentielle (version {d.situation.version})</CardTitle>
                <CardDescription>Réservée à l'agent, aux Ressources humaines, à la Division des investissements, au Directeur financier, au DGA Administration et Finances et au Directeur Général.</CardDescription>
              </CardHeader>
              <CardContent className="grid gap-3 text-sm sm:grid-cols-2 lg:grid-cols-4">
                <Champ l="Salaire net contractuel" v={d.situation.salaireNet === null ? '—' : montantDevise(d.situation.salaireNet, d.situation.devise as DevisePret)} />
                <Champ l="Départ à la retraite prévu" v={d.situation.dateRetraitePrevue ? dateFr(d.situation.dateRetraitePrevue) : '—'} />
                <Champ l="Décompte final estimé" v={d.situation.decompteFinalEstime === null ? '—' : montantDevise(d.situation.decompteFinalEstime, d.situation.devise as DevisePret)} />
                <Champ l="Date du calcul" v={d.situation.dateCalculDecompte ? dateFr(d.situation.dateCalculDecompte) : '—'} />
                {d.questionDisciplinaire !== null && <Champ l="Question disciplinaire (RH)" v={d.questionDisciplinaire ? 'Oui : mention au dossier' : 'Non'} />}
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="simulation" className="space-y-4">
          <Simulateur d={d} onglet={onglet} />
          {d.conditions.length > 0 && (
            <Card data-testid="conditions">
              <CardHeader>
                <CardTitle>Conditions et acceptations</CardTitle>
                <CardDescription>Version 1 : confirmation de l'agent ; toute modification par le Directeur financier ou le Directeur Général crée une version à accepter. Clauses d'acceptation en version {d.clause.version}.</CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Version</TableHead>
                      <TableHead>Auteur</TableHead>
                      <TableHead className="text-right">Montant</TableHead>
                      <TableHead className="text-right">Durée</TableHead>
                      <TableHead className="text-right">Mensualité</TableHead>
                      <TableHead className="text-right">Total à rembourser</TableHead>
                      <TableHead className="text-right">Net versé</TableHead>
                      <TableHead>Réponse</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {d.conditions.map((c) => (
                      <TableRow key={c.id}>
                        <TableCell>
                          v{c.version} {c.version === q.conditionsEnAttente ? <Badge variant="secondary">en attente</Badge> : c.version === d.conditionsCourantes?.version ? <Badge>en vigueur</Badge> : null}
                        </TableCell>
                        <TableCell>
                          {c.auteur === 'AGENT' ? 'Demandeur' : c.auteur === 'DIRECTEUR_FINANCIER' ? 'Directeur financier' : 'Directeur Général'} · {c.auteurNom}
                          {c.commentaire && <div className="text-xs text-muted-foreground">{c.commentaire}</div>}
                        </TableCell>
                        <TableCell className="num text-right">{montantDevise(c.montant, dev)}</TableCell>
                        <TableCell className="num text-right">{c.dureeMois} mois</TableCell>
                        <TableCell className="num text-right">{montantDevise(c.mensualite, dev)}</TableCell>
                        <TableCell className="num text-right">{montantDevise(c.total, dev)}</TableCell>
                        <TableCell className="num text-right">{montantDevise(c.montantNet, dev)}</TableCell>
                        <TableCell>{c.accepteeLe ? `Acceptée le ${dateHeureFr(c.accepteeLe)}` : c.refuseeLe ? `Refusée le ${dateHeureFr(c.refuseeLe)}` : 'En attente'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                {d.conditionsCourantes && <p className="mt-3 text-xs text-muted-foreground">{d.conditionsCourantes.clauseTexte}</p>}
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="examen" className="space-y-4">
          {d.controles.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Contrôles</CardTitle>
                <CardDescription>Garantie (exposition rapportée au décompte final) et capacité de remboursement (mensualités rapportées au salaire net), sur les conditions {d.conditionsEnAttente ? 'proposées' : 'en vigueur'}.</CardDescription>
              </CardHeader>
              <CardContent>
                <ControlesConge controles={d.controles} />
              </CardContent>
            </Card>
          )}
          {d.chiffres && (
            <Card data-testid="chiffres">
              <CardHeader>
                <CardTitle>Capacité de remboursement et garantie</CardTitle>
              </CardHeader>
              <CardContent className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                <Kpi label="Mensualités maintenues + nouvelle" valeur={montantDevise(d.chiffres.mensualitesMaintenues + d.chiffres.mensualite, dev)} detail={`${pct(d.chiffres.partSalaire)} du salaire net · seuil ${d.chiffres.seuilCapacite} %`} accent={d.chiffres.partSalaire !== null && d.chiffres.partSalaire > d.chiffres.seuilCapacite ? 'alerte' : 'ok'} />
                <Kpi label="Capacité résiduelle" valeur={d.chiffres.capaciteResiduelle === null ? '—' : montantDevise(d.chiffres.capaciteResiduelle, dev)} detail={d.chiffres.capaciteMax === null ? 'salaire non renseigné' : `capacité maximale ${montantDevise(d.chiffres.capaciteMax, dev)}`} />
                <Kpi label="Exposition totale" valeur={montantDevise(d.chiffres.encoursTotal + d.chiffres.totalARembourser, dev)} detail={`${pct(d.chiffres.partDecompte)} du décompte final · quotité ${d.chiffres.quotite} %`} accent={d.chiffres.partDecompte !== null && d.chiffres.partDecompte > d.parametres.quotiteMax ? 'critique' : d.chiffres.partDecompte !== null && d.chiffres.partDecompte > d.chiffres.quotite ? 'alerte' : 'ok'} />
                <Kpi label="Dernière échéance" valeur={dateFr(d.chiffres.derniereEcheance)} detail={d.chiffres.dateRetraitePrevue ? `retraite prévue le ${dateFr(d.chiffres.dateRetraitePrevue)}` : 'date de retraite non renseignée'} />
              </CardContent>
            </Card>
          )}
          <Card data-testid="avis">
            <CardHeader>
              <CardTitle>Avis d'étape</CardTitle>
              <CardDescription>Ressources humaines et Juridique, puis Direction financière ; motifs du formulaire.</CardDescription>
            </CardHeader>
            <CardContent>
              {d.avis.length === 0 ? (
                <p className="text-sm text-muted-foreground">Aucun avis rendu.</p>
              ) : (
                <ul className="space-y-2 text-sm">
                  {d.avis.map((v) => (
                    <li key={v.id} className="rounded-md border p-3">
                      <div className="flex flex-wrap items-center gap-2">
                        <Badge variant={v.sens === 'FAVORABLE' ? 'default' : 'destructive'}>{v.sens === 'FAVORABLE' ? 'Favorable' : 'Défavorable'}</Badge>
                        <span className="font-medium">{LIBELLE_ETAPE[v.etape]}</span>
                        <span className="text-muted-foreground">
                          {v.auteur}
                          {v.titulaire ? ` (pour ${v.titulaire})` : ''} · {dateHeureFr(v.date)}
                        </span>
                      </div>
                      {v.libellesMotifs.length > 0 && <div className="mt-1 text-muted-foreground">{v.libellesMotifs.join(' ; ')}</div>}
                      {v.commentaire && <div className="mt-1">{v.commentaire}</div>}
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>
          {(d.analyse || a.analyser || a.affecter) && (
            <Card data-testid="analyse">
              <CardHeader>
                <CardTitle className="flex flex-wrap items-center justify-between gap-2">
                  Fiche d'analyse de la Division des investissements
                  {d.analyse && <BoutonPdf chemin={`/pdf/prets/${q.id}/analyse`} nom={`${q.numero ?? 'pret'}-analyse.pdf`} libelle="Fiche PDF" taille="sm" />}
                </CardTitle>
                <CardDescription>
                  {q.instructeur ? `Assignée à ${q.instructeur.nom}. ` : 'Aucun instructeur affecté. '}
                  Générée par l'application, signée par l'analyste sans signature d'approbation (point 28) ; réservée aux intervenants.
                </CardDescription>
              </CardHeader>
              {d.analyse && (
                <CardContent className="space-y-3 text-sm">
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge variant={d.analyse.avis === 'FAVORABLE' ? 'default' : 'destructive'}>Avis {d.analyse.avis === 'FAVORABLE' ? 'favorable' : 'défavorable'}</Badge>
                    <span className="text-muted-foreground">
                      version {d.analyse.version} · {d.analyse.analyste} · {d.analyse.analysteFonction} · {dateHeureFr(d.analyse.date)}
                    </span>
                  </div>
                  <p>{d.analyse.commentaire}</p>
                  <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                    <Champ l="Montant total du prêt" v={montantDevise(d.analyse.chiffres.montantTotal, dev)} />
                    <Champ l="Montant affecté au rachat" v={montantDevise(d.analyse.chiffres.montantRachat, dev)} />
                    <Champ l="Montant net versé" v={montantDevise(d.analyse.chiffres.montantNet, dev)} />
                    <Champ l="Total à rembourser" v={montantDevise(d.analyse.chiffres.totalARembourser, dev)} />
                    <Champ l="Total des intérêts" v={`${montantDevise(d.analyse.chiffres.totalInterets, dev)} (${d.analyse.chiffres.tauxAnnuel} % l'an)`} />
                    <Champ l="Mensualité · maturité" v={`${montantDevise(d.analyse.chiffres.mensualite, dev)} · ${d.analyse.chiffres.maturite} mois`} />
                    <Champ l="Part du salaire net" v={pct(d.analyse.chiffres.partSalaire)} />
                    <Champ l="Part du décompte final" v={pct(d.analyse.chiffres.partDecompte)} />
                    <Champ l="Encours cumulés" v={montantDevise(d.analyse.chiffres.encoursTotal, dev)} />
                  </div>
                </CardContent>
              )}
            </Card>
          )}
        </TabsContent>

        <TabsContent value="circuit" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Circuit de validation</CardTitle>
              {!d.circuit && <CardDescription>Circuit prévu à la confirmation du dossier : Ressources humaines et Juridique, Direction financière, DGA Administration et Finances, Directeur Général.</CardDescription>}
            </CardHeader>
            <CardContent>{d.circuit ? <CircuitTimeline circuit={d.circuit} /> : <EtapesPrevues etapes={d.circuitPrevu} />}</CardContent>
          </Card>
          {d.circuit && d.circuit.history.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Historique</CardTitle>
              </CardHeader>
              <CardContent>
                <Historique circuit={d.circuit} />
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="remboursement">{onglet === 'remboursement' && <PretAccorde demandeId={q.id} />}</TabsContent>

        <TabsContent value="pieces">
          <PiecesJointes objetType="PRET" objetId={q.id} typesSuggeres={['JUSTIFICATIF', 'CONTRAT', 'COURRIER', 'AUTRE']} description="Pièces du dossier à accès restreint : agent, Ressources humaines, Division des investissements, Direction financière, DGA Administration et Finances, Directeur Général." />
        </TabsContent>
      </Tabs>

      <PlafondDialog d={d} ouvert={dialogue === 'plafond'} onClose={() => setDialogue(null)} onConfirmer={(corps) => executer(() => actions.plafond.mutateAsync({ id: q.id, corps }), 'Plafond communiqué à l’agent.')} chargement={actions.plafond.isPending} />
      <ConfirmationPretDialog d={d} ouvert={dialogue === 'confirmer'} onClose={() => setDialogue(null)} onConfirmer={(simulationId, rachats, commentaire) => executer(() => actions.confirmer.mutateAsync({ id: q.id, simulationId, rachats, commentaire }), 'Dossier confirmé et soumis aux Ressources humaines et Juridique.')} chargement={actions.confirmer.isPending} />
      <DecisionPretDialog d={d} ouvert={dialogue === 'decision'} onClose={() => setDialogue(null)} onConfirmer={(decision, motif, avis) => executer(() => actions.decider.mutateAsync({ id: q.id, decision, motif, avis }), 'Décision enregistrée.')} chargement={actions.decider.isPending} />
      <AffectationDialog ouvert={dialogue === 'affecter'} onClose={() => setDialogue(null)} onConfirmer={(instructeurId) => executer(() => actions.affecter.mutateAsync({ id: q.id, instructeurId }), 'Dossier affecté pour analyse.')} chargement={actions.affecter.isPending} />
      <TexteDialog ouvert={dialogue === 'analyse'} onClose={() => setDialogue(null)} titre="Fiche d'analyse" description="Les chiffres sont générés par l'application à partir du dossier ; vous formulez l'avis motivé de l'analyste." libelle="Établir la fiche" avecSens minimum={10} onConfirmer={(texte, sens) => executer(() => actions.analyser.mutateAsync({ id: q.id, avis: sens ?? 'FAVORABLE', commentaire: texte }), 'Fiche d’analyse établie ; la Direction financière est informée.')} chargement={actions.analyser.isPending} />
      <ConditionsDialog d={d} ouvert={dialogue === 'conditions'} onClose={() => setDialogue(null)} onConfirmer={(montant, dureeMois, commentaire) => executer(() => actions.conditions.mutateAsync({ id: q.id, montant, dureeMois, commentaire }), 'Nouvelles conditions proposées à l’agent ; le circuit attend sa réponse.')} chargement={actions.conditions.isPending} />
      <ReponseConditionsDialog d={d} ouvert={dialogue === 'reponse'} onClose={() => setDialogue(null)} onConfirmer={(accepte, commentaire) => executer(() => actions.repondreConditions.mutateAsync({ id: q.id, version: d.conditionsEnAttente?.version ?? 0, accepte, commentaire }), accepte ? 'Conditions acceptées : le circuit reprend.' : 'Conditions refusées : la demande est annulée.')} chargement={actions.repondreConditions.isPending} />
      <TexteDialog ouvert={dialogue === 'suspendre'} onClose={() => setDialogue(null)} titre="Suspendre le dossier" description="Motif obligatoire (10 caractères au moins) ; l'agent, les RH et la Division des investissements sont informés." libelle="Suspendre" minimum={10} onConfirmer={(motif) => executer(() => actions.suspendre.mutateAsync({ id: q.id, motif }), 'Dossier suspendu.')} chargement={actions.suspendre.isPending} />
      <TexteDialog ouvert={dialogue === 'retirer'} onClose={() => setDialogue(null)} titre="Retirer la demande" description="La demande est annulée ; les engagements marqués « rachat prévu » redeviennent en cours." libelle="Retirer" minimum={0} onConfirmer={(motif) => executer(() => actions.retirer.mutateAsync({ id: q.id, motif: motif || undefined }), 'Demande retirée.')} chargement={actions.retirer.isPending} />
      <TexteDialog ouvert={dialogue === 'resoumettre'} onClose={() => setDialogue(null)} titre="Soumettre le complément" description="Indiquez ce qui a été complété (engagements, pièces) ; le dossier reprend le circuit à l'étape qui a demandé le complément." libelle="Soumettre" minimum={0} onConfirmer={(reponse) => executer(() => actions.resoumettre.mutateAsync({ id: q.id, reponse: reponse || undefined }), 'Complément soumis.')} chargement={actions.resoumettre.isPending} />
      <EngagementsDialog d={d} ouvert={dialogue === 'engagements'} onClose={() => setDialogue(null)} onConfirmer={(engagements) => executer(() => actions.engagements.mutateAsync({ id: q.id, engagements }), 'Engagements enregistrés.')} chargement={actions.engagements.isPending} />
    </div>
  );
}

function Champ({ l, v }: { l: string; v: ReactNode }) {
  return (
    <div>
      <span className="text-muted-foreground">{l}</span>
      <div>{v}</div>
    </div>
  );
}

function TableauEcheancier({ echeances, devise }: { echeances: EcheancePret[]; devise: DevisePret }) {
  return (
    <div className="max-h-96 overflow-auto rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>N°</TableHead>
            <TableHead>Échéance</TableHead>
            <TableHead className="text-right">Capital en début</TableHead>
            <TableHead className="text-right">Intérêts</TableHead>
            <TableHead className="text-right">Principal</TableHead>
            <TableHead className="text-right">Mensualité</TableHead>
            <TableHead className="text-right">Capital restant dû</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {echeances.map((e) => (
            <TableRow key={e.numero}>
              <TableCell>{e.numero}</TableCell>
              <TableCell>{dateFr(e.date)}</TableCell>
              <TableCell className="num text-right">{montantDevise(e.capitalDebut, devise)}</TableCell>
              <TableCell className="num text-right">{montantDevise(e.interets, devise)}</TableCell>
              <TableCell className="num text-right">{montantDevise(e.principal, devise)}</TableCell>
              <TableCell className="num text-right">{montantDevise(e.mensualite, devise)}</TableCell>
              <TableCell className="num text-right">{montantDevise(e.capitalFin, devise)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

/** Simulateur (§ 5.5) : montant et durée, mensualité, intérêts, total, parts, échéancier ; simulations comparées. */
function Simulateur({ d, onglet }: { d: DetailPret; onglet: string }) {
  const q = d.demande;
  const dev = q.devise;
  const actions = useActionsPret();
  const [montant, setMontant] = useState(String(q.montantConfirme ?? q.montantSouhaite));
  const [duree, setDuree] = useState(String(q.dureeMois ?? d.parametres.dureeMaxMois));
  const [dernier, setDernier] = useState<ResultatSimulation | null>(null);
  const [choisie, setChoisie] = useState<string | undefined>(undefined);
  const echeancier = useEcheancierPret(q.id, choisie, onglet === 'simulation' && (Boolean(choisie) || Boolean(d.conditionsCourantes)));
  const simuler = async () => {
    try {
      const r = await actions.simuler.mutateAsync({ id: q.id, montant: Number(montant), dureeMois: Number(duree) });
      setDernier(r);
      setChoisie(r.simulation.id);
      toast.success('Simulation enregistrée.');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Simulation impossible.');
    }
  };
  return (
    <>
      {d.actions.simuler && (
        <Card data-testid="simulateur">
          <CardHeader>
            <CardTitle>Simuler</CardTitle>
            <CardDescription>
              Annuité à {d.parametres.tauxAnnuel} % l'an, intérêts sur le capital restant dû, {d.parametres.dureeMaxMois} mois au plus ; le total à rembourser doit rester dans le plafond ({d.plafond ? montantDevise(d.plafond.plafondDisponible, dev) : '—'}) et la mensualité dans {d.parametres.seuilCapacite} % du salaire net. Première échéance à la fin du mois suivant le décaissement.
            </CardDescription>
          </CardHeader>
          <CardContent className="flex flex-wrap items-end gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="sim-montant">Montant financé ({dev})</Label>
              <Input id="sim-montant" type="number" min={1} step="0.01" className="w-40" value={montant} onChange={(e) => setMontant(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="sim-duree">Durée (mois)</Label>
              <Input id="sim-duree" type="number" min={1} max={d.parametres.dureeMaxMois} className="w-28" value={duree} onChange={(e) => setDuree(e.target.value)} />
            </div>
            <Button onClick={simuler} disabled={!(Number(montant) > 0) || !(Number(duree) >= 1) || actions.simuler.isPending}>
              Simuler
            </Button>
          </CardContent>
        </Card>
      )}
      {dernier && (
        <Card data-testid="resultat-simulation">
          <CardHeader>
            <CardTitle>Dernière simulation</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              <Kpi label="Mensualité" valeur={montantDevise(dernier.simulation.mensualite, dev)} detail={`${dernier.simulation.dureeMois} mois à ${dernier.simulation.tauxAnnuel} %`} />
              <Kpi label="Total des intérêts" valeur={montantDevise(dernier.simulation.totalInterets, dev)} detail="aucun frais de dossier" />
              <Kpi label="Total à rembourser" valeur={montantDevise(dernier.simulation.total, dev)} detail={dernier.simulation.dansPlafond ? 'dans le plafond' : 'au-delà du plafond'} accent={dernier.simulation.dansPlafond ? 'ok' : 'critique'} />
              <Kpi label="Part du salaire net" valeur={pct(dernier.simulation.partSalaire)} detail={dernier.simulation.capaciteOk === false ? `au-delà de ${d.parametres.seuilCapacite} % : avis RH défavorable obligatoire` : `seuil ${d.parametres.seuilCapacite} %`} accent={dernier.simulation.capaciteOk === false ? 'alerte' : 'ok'} />
            </div>
            <ControlesConge controles={dernier.controles} />
          </CardContent>
        </Card>
      )}
      <Card data-testid="simulations">
        <CardHeader>
          <CardTitle>Simulations comparées</CardTitle>
          <CardDescription>Chaque simulation affiche sa part du salaire et sa part du décompte final ; choisissez-en une pour afficher son échéancier.</CardDescription>
        </CardHeader>
        <CardContent>
          {d.simulations.length === 0 ? (
            <p className="text-sm text-muted-foreground">Aucune simulation enregistrée.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead />
                  <TableHead className="text-right">Montant</TableHead>
                  <TableHead className="text-right">Durée</TableHead>
                  <TableHead className="text-right">Mensualité</TableHead>
                  <TableHead className="text-right">Intérêts</TableHead>
                  <TableHead className="text-right">Total</TableHead>
                  <TableHead className="text-right">Part du salaire</TableHead>
                  <TableHead className="text-right">Part du décompte</TableHead>
                  <TableHead>Échéances</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {d.simulations.map((s) => (
                  <TableRow key={s.id} className={choisie === s.id ? 'bg-muted/50' : undefined}>
                    <TableCell>
                      <Button variant="ghost" size="sm" onClick={() => setChoisie(s.id)}>
                        Voir
                      </Button>
                    </TableCell>
                    <TableCell className="num text-right">{montantDevise(s.montant, dev)}</TableCell>
                    <TableCell className="num text-right">{s.dureeMois} mois</TableCell>
                    <TableCell className="num text-right">{montantDevise(s.mensualite, dev)}</TableCell>
                    <TableCell className="num text-right">{montantDevise(s.totalInterets, dev)}</TableCell>
                    <TableCell className="num text-right">{montantDevise(s.total, dev)}</TableCell>
                    <TableCell className="num text-right">{pct(s.partSalaire)}</TableCell>
                    <TableCell className="num text-right">{pct(s.partDecompte)}</TableCell>
                    <TableCell className="text-xs">
                      du {dateFr(s.premiereEcheance)} au {dateFr(s.derniereEcheance)}
                    </TableCell>
                    <TableCell className="space-x-1">
                      {s.retenue && <Badge>retenue</Badge>}
                      {!s.dansPlafond && <Badge variant="destructive">hors plafond</Badge>}
                      {s.capaciteOk === false && <Badge variant="secondary">capacité dépassée</Badge>}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
      {(choisie || d.conditionsCourantes) && echeancier.data && (
        <Card data-testid="echeancier">
          <CardHeader>
            <CardTitle className="flex flex-wrap items-center justify-between gap-2">
              {echeancier.data.titre}
              <BoutonExcel chemin={`/rh/prets/${q.id}/echeancier.xlsx${choisie ? `?simulationId=${choisie}` : ''}`} nom={`${q.numero ?? 'pret'}-echeancier.xlsx`} />
            </CardTitle>
            <CardDescription>
              {montantDevise(echeancier.data.montant, dev)} sur {echeancier.data.dureeMois} mois à {echeancier.data.tauxAnnuel} % l'an · plan prévisionnel établi à la date du jour, figé au décaissement.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <TableauEcheancier echeances={echeancier.data.echeances} devise={dev} />
          </CardContent>
        </Card>
      )}
      {d.echeancier && !choisie && !d.conditionsCourantes && null}
    </>
  );
}

function PlafondDialog({ d, ouvert, onClose, onConfirmer, chargement }: { d: DetailPret; ouvert: boolean; onClose: () => void; onConfirmer: (corps: { decompteFinal: number; dateCalcul: string; quotite?: number; observations?: string; salaireNet?: number | null; dateRetraitePrevue?: string | null }) => void; chargement: boolean }) {
  const [decompte, setDecompte] = useState(String(d.situation?.decompteFinalEstime ?? ''));
  const [dateCalcul, setDateCalcul] = useState(auj());
  const [quotite, setQuotite] = useState(String(d.plafond?.quotite ?? d.parametres.quotiteDefaut));
  const [observations, setObservations] = useState('');
  const [salaire, setSalaire] = useState(String(d.situation?.salaireNet ?? ''));
  const [retraite, setRetraite] = useState(d.situation?.dateRetraitePrevue ?? '');
  return (
    <Dialog open={ouvert} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>Plafond de la demande {d.demande.numero}</DialogTitle>
          <DialogDescription>Le décompte final est calculé manuellement par les Ressources humaines et conservé dans la situation confidentielle de l'agent avec sa date. Plafond = décompte final × quotité − capacité déjà mobilisée ; valable {d.parametres.validitePlafondJours} jours.</DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor="pl-decompte">Décompte final estimé ({d.demande.devise})</Label>
            <Input id="pl-decompte" type="number" min={0} step="0.01" value={decompte} onChange={(e) => setDecompte(e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="pl-date">Date du calcul</Label>
            <Input id="pl-date" type="date" value={dateCalcul} onChange={(e) => setDateCalcul(e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="pl-quotite">Quotité mobilisable (%)</Label>
            <Input id="pl-quotite" type="number" min={0} max={d.parametres.quotiteMax} value={quotite} onChange={(e) => setQuotite(e.target.value)} />
            <p className="text-xs text-muted-foreground">
              {d.parametres.quotiteDefaut} % par défaut, {d.parametres.quotiteMax} % au plus.
            </p>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="pl-salaire">Salaire net contractuel (mise à jour facultative)</Label>
            <Input id="pl-salaire" type="number" min={0} step="0.01" value={salaire} onChange={(e) => setSalaire(e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="pl-retraite">Départ à la retraite prévu</Label>
            <Input id="pl-retraite" type="date" value={retraite} onChange={(e) => setRetraite(e.target.value)} />
          </div>
          <div className="space-y-1.5 sm:col-span-2">
            <Label htmlFor="pl-obs">Observations</Label>
            <Textarea id="pl-obs" rows={2} value={observations} onChange={(e) => setObservations(e.target.value)} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Annuler
          </Button>
          <Button onClick={() => onConfirmer({ decompteFinal: Number(decompte), dateCalcul, quotite: Number(quotite), observations: observations || undefined, salaireNet: salaire === '' ? undefined : Number(salaire), dateRetraitePrevue: retraite || undefined })} disabled={!(Number(decompte) >= 0) || !dateCalcul || chargement}>
            Communiquer le plafond
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ConfirmationPretDialog({ d, ouvert, onClose, onConfirmer, chargement }: { d: DetailPret; ouvert: boolean; onClose: () => void; onConfirmer: (simulationId: string, rachats: string[], commentaire?: string) => void; chargement: boolean }) {
  const dev = d.demande.devise;
  const [simulationId, setSimulationId] = useState(d.simulations.find((s) => s.dansPlafond)?.id ?? d.simulations[0]?.id ?? '');
  const [rachats, setRachats] = useState<string[]>(d.engagements.filter((e) => e.statut === 'RACHAT_PREVU').map((e) => e.id));
  const [lu, setLu] = useState(false);
  const [commentaire, setCommentaire] = useState('');
  const s = d.simulations.find((x) => x.id === simulationId);
  return (
    <Dialog open={ouvert} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Confirmer le dossier de prêt</DialogTitle>
          <DialogDescription>Choisissez la simulation retenue, les engagements rachetés le cas échéant, puis acceptez les clauses : la version 1 des conditions est horodatée et le dossier part aux Ressources humaines et Juridique.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="cf-sim">Simulation retenue</Label>
            <SelectNatif id="cf-sim" value={simulationId} onChange={(e) => setSimulationId(e.target.value)}>
              {d.simulations.map((x) => (
                <option key={x.id} value={x.id}>
                  {montantDevise(x.montant, dev)} sur {x.dureeMois} mois · {montantDevise(x.mensualite, dev)} par mois · total {montantDevise(x.total, dev)}
                  {x.dansPlafond ? '' : ' · hors plafond'}
                </option>
              ))}
            </SelectNatif>
            {s && !s.dansPlafond && <p className="text-xs text-destructive">Cette simulation dépasse le plafond : réduisez le montant ou la durée.</p>}
          </div>
          {d.engagements.length > 0 && (
            <div className="space-y-2">
              <Label>Engagements rachetés par ce prêt (point 26)</Label>
              {d.engagements
                .filter((e) => e.statut !== 'ETEINT')
                .map((e) => (
                  <label key={e.id} className="flex items-center gap-2 text-sm">
                    <Checkbox checked={rachats.includes(e.id)} onCheckedChange={(v) => setRachats(v ? [...rachats, e.id] : rachats.filter((x) => x !== e.id))} />
                    {e.source === 'FONAREV' ? 'FONAREV' : e.banque} · solde restant dû {montantDevise(e.soldeRestantDu, e.devise)}
                  </label>
                ))}
            </div>
          )}
          <div className="rounded-md border bg-muted/40 p-3 text-sm">
            <p className="font-medium">Clauses d'acceptation (version {d.clause.version})</p>
            <p className="mt-1">{d.clause.texte}</p>
          </div>
          <label className="flex items-start gap-2 text-sm">
            <Checkbox checked={lu} onCheckedChange={(v) => setLu(Boolean(v))} />
            <span>Lu et approuvé : j'accepte les conditions (mensualité, montant total et frais) et les clauses ci-dessus.</span>
          </label>
          <div className="space-y-1.5">
            <Label htmlFor="cf-com">Commentaire (facultatif)</Label>
            <Textarea id="cf-com" rows={2} value={commentaire} onChange={(e) => setCommentaire(e.target.value)} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Annuler
          </Button>
          <Button onClick={() => onConfirmer(simulationId, rachats, commentaire || undefined)} disabled={!simulationId || !lu || chargement}>
            Confirmer et soumettre
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DecisionPretDialog({ d, ouvert, onClose, onConfirmer, chargement }: { d: DetailPret; ouvert: boolean; onClose: () => void; onConfirmer: (decision: DecisionApi, motif?: string, avis?: { sens: SensAvis; motifs?: MotifAvis[]; commentaire?: string }) => void; chargement: boolean }) {
  const a = d.actions;
  const [decision, setDecision] = useState<DecisionApi>(a.decisions[0] ?? 'VALIDER');
  const [motif, setMotif] = useState('');
  const [sens, setSens] = useState<SensAvis>(a.avisDefavorableRequis ? 'DEFAVORABLE' : 'FAVORABLE');
  const [motifs, setMotifs] = useState<MotifAvis[]>(a.avisDefavorableRequis ? ['RATIO'] : []);
  const [commentaire, setCommentaire] = useState('');
  const avecAvis = a.avisRequis && (decision === 'VALIDER' || decision === 'REJETER');
  return (
    <Dialog open={ouvert} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>Décision · {a.etape ? LIBELLE_ETAPE[a.etape] : ''}</DialogTitle>
          <DialogDescription>Votre décision est signée électroniquement et inscrite au journal d'audit.{a.avisRequis ? ' À cette étape, la validation s’accompagne d’un avis favorable ou défavorable motivé.' : ''}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="flex flex-wrap gap-2">
            {a.decisions.map((x) => (
              <Button key={x} type="button" variant={decision === x ? 'default' : 'outline'} size="sm" onClick={() => setDecision(x)}>
                {LIBELLE_DECISION[x]}
              </Button>
            ))}
          </div>
          {avecAvis && (
            <div className="space-y-2 rounded-md border p-3">
              <div className="flex flex-wrap items-center gap-2">
                <Label>Avis</Label>
                <Button type="button" size="sm" variant={sens === 'FAVORABLE' ? 'default' : 'outline'} onClick={() => setSens('FAVORABLE')} disabled={a.avisDefavorableRequis}>
                  Favorable
                </Button>
                <Button type="button" size="sm" variant={sens === 'DEFAVORABLE' ? 'destructive' : 'outline'} onClick={() => setSens('DEFAVORABLE')}>
                  Défavorable
                </Button>
              </div>
              {a.avisDefavorableRequis && <p className="text-xs text-amber-700">Avis défavorable obligatoire : capacité de remboursement au-delà du seuil ou dernière échéance après la retraite (point 20) ; la décision finale appartient au Directeur Général.</p>}
              {sens === 'DEFAVORABLE' && (
                <div className="grid gap-1 sm:grid-cols-2">
                  {MOTIFS_AVIS.map((m) => (
                    <label key={m} className="flex items-center gap-2 text-sm">
                      <Checkbox checked={motifs.includes(m)} onCheckedChange={(v) => setMotifs(v ? [...motifs, m] : motifs.filter((x) => x !== m))} />
                      {LIBELLE_MOTIF_AVIS[m]}
                    </label>
                  ))}
                </div>
              )}
              <div className="space-y-1.5">
                <Label htmlFor="av-com">Commentaire de l'avis</Label>
                <Textarea id="av-com" rows={2} value={commentaire} onChange={(e) => setCommentaire(e.target.value)} />
              </div>
            </div>
          )}
          <div className="space-y-1.5">
            <Label htmlFor="dec-motif">{decision === 'VALIDER' ? 'Commentaire (facultatif)' : 'Motif'}</Label>
            <Textarea id="dec-motif" rows={2} value={motif} onChange={(e) => setMotif(e.target.value)} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Fermer
          </Button>
          <Button onClick={() => onConfirmer(decision, motif || undefined, avecAvis ? { sens, motifs, commentaire: commentaire || undefined } : undefined)} disabled={chargement || (decision !== 'VALIDER' && !motif.trim()) || (avecAvis && sens === 'DEFAVORABLE' && !motifs.length && !commentaire.trim())}>
            {LIBELLE_DECISION[decision]}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AffectationDialog({ ouvert, onClose, onConfirmer, chargement }: { ouvert: boolean; onClose: () => void; onConfirmer: (instructeurId: string) => void; chargement: boolean }) {
  const instructeurs = useInstructeursPrets(ouvert);
  const [id, setId] = useState('');
  return (
    <Dialog open={ouvert} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Affecter le dossier pour analyse</DialogTitle>
          <DialogDescription>Le superviseur de la Division des investissements établit la fiche d'analyse et son avis motivé, signés comme analyste, sans signature d'approbation.</DialogDescription>
        </DialogHeader>
        <SelectNatif value={id} onChange={(e) => setId(e.target.value)}>
          <option value="">Choisir un superviseur</option>
          {(instructeurs.data ?? []).map((i) => (
            <option key={i.id} value={i.id}>
              {i.nom} · {i.fonction}
            </option>
          ))}
        </SelectNatif>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Annuler
          </Button>
          <Button onClick={() => onConfirmer(id)} disabled={!id || chargement}>
            Affecter
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function TexteDialog({ ouvert, onClose, titre, description, libelle, minimum, avecSens, onConfirmer, chargement }: { ouvert: boolean; onClose: () => void; titre: string; description: string; libelle: string; minimum: number; avecSens?: boolean; onConfirmer: (texte: string, sens?: SensAvis) => void; chargement: boolean }) {
  const [texte, setTexte] = useState('');
  const [sens, setSens] = useState<SensAvis>('FAVORABLE');
  return (
    <Dialog open={ouvert} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{titre}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        {avecSens && (
          <div className="flex flex-wrap items-center gap-2">
            <Label>Avis</Label>
            <Button type="button" size="sm" variant={sens === 'FAVORABLE' ? 'default' : 'outline'} onClick={() => setSens('FAVORABLE')}>
              Favorable
            </Button>
            <Button type="button" size="sm" variant={sens === 'DEFAVORABLE' ? 'destructive' : 'outline'} onClick={() => setSens('DEFAVORABLE')}>
              Défavorable
            </Button>
          </div>
        )}
        <Textarea rows={4} value={texte} onChange={(e) => setTexte(e.target.value)} placeholder={minimum ? `${minimum} caractères au moins` : 'Facultatif'} />
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Annuler
          </Button>
          <Button onClick={() => onConfirmer(texte.trim(), avecSens ? sens : undefined)} disabled={texte.trim().length < minimum || chargement}>
            {libelle}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ConditionsDialog({ d, ouvert, onClose, onConfirmer, chargement }: { d: DetailPret; ouvert: boolean; onClose: () => void; onConfirmer: (montant: number, dureeMois: number, commentaire: string) => void; chargement: boolean }) {
  const [montant, setMontant] = useState(String(d.conditionsCourantes?.montant ?? d.demande.montantSouhaite));
  const [duree, setDuree] = useState(String(d.conditionsCourantes?.dureeMois ?? d.parametres.dureeMaxMois));
  const [commentaire, setCommentaire] = useState('');
  return (
    <Dialog open={ouvert} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Modifier les conditions</DialogTitle>
          <DialogDescription>Une nouvelle version des conditions est proposée à l'agent ; le circuit est suspendu jusqu'à son acceptation (un refus annule la demande). Les contrôles de plafond et de garantie s'appliquent.</DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor="cd-montant">Montant ({d.demande.devise})</Label>
            <Input id="cd-montant" type="number" min={1} step="0.01" value={montant} onChange={(e) => setMontant(e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="cd-duree">Durée (mois)</Label>
            <Input id="cd-duree" type="number" min={1} max={d.parametres.dureeMaxMois} value={duree} onChange={(e) => setDuree(e.target.value)} />
          </div>
          <div className="space-y-1.5 sm:col-span-2">
            <Label htmlFor="cd-com">Motif de la modification (10 caractères au moins)</Label>
            <Textarea id="cd-com" rows={3} value={commentaire} onChange={(e) => setCommentaire(e.target.value)} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Annuler
          </Button>
          <Button onClick={() => onConfirmer(Number(montant), Number(duree), commentaire.trim())} disabled={!(Number(montant) > 0) || !(Number(duree) >= 1) || commentaire.trim().length < 10 || chargement}>
            Proposer à l'agent
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ReponseConditionsDialog({ d, ouvert, onClose, onConfirmer, chargement }: { d: DetailPret; ouvert: boolean; onClose: () => void; onConfirmer: (accepte: boolean, commentaire?: string) => void; chargement: boolean }) {
  const c = d.conditionsEnAttente;
  const [lu, setLu] = useState(false);
  const [commentaire, setCommentaire] = useState('');
  if (!c) return null;
  const dev = d.demande.devise;
  return (
    <Dialog open={ouvert} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>Conditions proposées (version {c.version})</DialogTitle>
          <DialogDescription>
            {montantDevise(c.montant, dev)} sur {c.dureeMois} mois à {c.tauxAnnuel} % l'an : mensualité {montantDevise(c.mensualite, dev)}, intérêts {montantDevise(c.totalInterets, dev)}, total à rembourser {montantDevise(c.total, dev)}, montant net versé {montantDevise(c.montantNet, dev)}.{c.commentaire ? ` Motif : ${c.commentaire}` : ''}
          </DialogDescription>
        </DialogHeader>
        <div className="rounded-md border bg-muted/40 p-3 text-sm">{c.clauseTexte}</div>
        <label className="flex items-start gap-2 text-sm">
          <Checkbox checked={lu} onCheckedChange={(v) => setLu(Boolean(v))} />
          <span>Lu et approuvé : j'accepte ces conditions et les clauses ci-dessus.</span>
        </label>
        <Textarea rows={2} placeholder="Commentaire (facultatif)" value={commentaire} onChange={(e) => setCommentaire(e.target.value)} />
        <DialogFooter>
          <Button variant="outline" onClick={() => onConfirmer(false, commentaire || undefined)} disabled={chargement}>
            Refuser (annule la demande)
          </Button>
          <Button onClick={() => onConfirmer(true, commentaire || undefined)} disabled={!lu || chargement}>
            Accepter
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

type LigneEng = { source: 'BANQUE' | 'FONAREV'; banque: string; montant: string; devise: DevisePret; mensualite: string; maturiteMois: string; dateDecaissement: string; soldeRestantDu: string; rachatPrevu: boolean };

function EngagementsDialog({ d, ouvert, onClose, onConfirmer, chargement }: { d: DetailPret; ouvert: boolean; onClose: () => void; onConfirmer: (engagements: EngagementSaisie[]) => void; chargement: boolean }) {
  const [lignes, setLignes] = useState<LigneEng[]>(d.engagements.map((e) => ({ source: e.source, banque: e.banque ?? '', montant: String(e.montant), devise: e.devise, mensualite: String(e.mensualite), maturiteMois: String(e.maturiteMois), dateDecaissement: e.dateDecaissement ?? '', soldeRestantDu: String(e.soldeRestantDu), rachatPrevu: e.statut === 'RACHAT_PREVU' })));
  const maj = (i: number, patch: Partial<LigneEng>) => setLignes(lignes.map((l, k) => (k === i ? { ...l, ...patch } : l)));
  const valide = lignes.every((l) => Number(l.montant) >= 0 && Number(l.mensualite) >= 0 && Number(l.maturiteMois) >= 1 && Number(l.soldeRestantDu) >= 0 && (l.source !== 'BANQUE' || l.banque.trim()));
  return (
    <Dialog open={ouvert} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-4xl">
        <DialogHeader>
          <DialogTitle>Engagements existants</DialogTitle>
          <DialogDescription>Prêts bancaires et FONAREV en cours : montant initial, mensualité, maturité, décaissement, solde restant dû. Les Ressources humaines les vérifient au calcul du plafond.</DialogDescription>
        </DialogHeader>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Source</TableHead>
                <TableHead>Banque</TableHead>
                <TableHead>Montant</TableHead>
                <TableHead>Devise</TableHead>
                <TableHead>Mensualité</TableHead>
                <TableHead>Maturité</TableHead>
                <TableHead>Décaissement</TableHead>
                <TableHead>Solde dû</TableHead>
                <TableHead>Rachat</TableHead>
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {lignes.map((l, i) => (
                <TableRow key={i}>
                  <TableCell>
                    <SelectNatif value={l.source} onChange={(e) => maj(i, { source: e.target.value as LigneEng['source'] })}>
                      <option value="BANQUE">Banque</option>
                      <option value="FONAREV">FONAREV</option>
                    </SelectNatif>
                  </TableCell>
                  <TableCell>
                    <Input value={l.banque} disabled={l.source !== 'BANQUE'} onChange={(e) => maj(i, { banque: e.target.value })} />
                  </TableCell>
                  <TableCell>
                    <Input type="number" min={0} step="0.01" className="w-24" value={l.montant} onChange={(e) => maj(i, { montant: e.target.value })} />
                  </TableCell>
                  <TableCell>
                    <SelectNatif value={l.devise} onChange={(e) => maj(i, { devise: e.target.value as DevisePret })}>
                      <option value="USD">USD</option>
                      <option value="CDF">CDF</option>
                    </SelectNatif>
                  </TableCell>
                  <TableCell>
                    <Input type="number" min={0} step="0.01" className="w-24" value={l.mensualite} onChange={(e) => maj(i, { mensualite: e.target.value })} />
                  </TableCell>
                  <TableCell>
                    <Input type="number" min={1} className="w-16" value={l.maturiteMois} onChange={(e) => maj(i, { maturiteMois: e.target.value })} />
                  </TableCell>
                  <TableCell>
                    <Input type="date" value={l.dateDecaissement} onChange={(e) => maj(i, { dateDecaissement: e.target.value })} />
                  </TableCell>
                  <TableCell>
                    <Input type="number" min={0} step="0.01" className="w-24" value={l.soldeRestantDu} onChange={(e) => maj(i, { soldeRestantDu: e.target.value })} />
                  </TableCell>
                  <TableCell>
                    <Checkbox checked={l.rachatPrevu} onCheckedChange={(v) => maj(i, { rachatPrevu: Boolean(v) })} />
                  </TableCell>
                  <TableCell>
                    <Button variant="ghost" size="icon" aria-label="Retirer" onClick={() => setLignes(lignes.filter((_, k) => k !== i))}>
                      <Trash2 className="size-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        <Button size="sm" variant="outline" onClick={() => setLignes([...lignes, { source: 'BANQUE', banque: '', montant: '', devise: d.demande.devise, mensualite: '', maturiteMois: '', dateDecaissement: '', soldeRestantDu: '', rachatPrevu: false }])}>
          <Plus className="size-4" /> Ajouter
        </Button>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Annuler
          </Button>
          <Button onClick={() => onConfirmer(lignes.map((l) => ({ source: l.source, banque: l.source === 'BANQUE' ? l.banque : null, montant: Number(l.montant), devise: l.devise, mensualite: Number(l.mensualite), maturiteMois: Number(l.maturiteMois), dateDecaissement: l.dateDecaissement || null, soldeRestantDu: Number(l.soldeRestantDu), rachatPrevu: l.rachatPrevu })))} disabled={!valide || chargement}>
            Enregistrer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
